/**
 * EAA-1212: the upload-limits seam.
 *
 * Shared packages (Tier 1/2) must never call the API, yet several of them hard-code a
 * file-size cap or an `accept` list that the server now lets a platform admin change.
 * The app fetches the configured limits (`GET /api/v1/config/upload-limits`) and feeds
 * them in through `UploadLimitsProvider`; every upload site reads them back here.
 *
 * Every field is NULLABLE on purpose. `null` means "the server has not told us" — while
 * loading, on error, or in an app that mounts no provider — and each site then keeps
 * its own hard-coded constant, exactly as before this seam existed. The constants
 * differ per site (a 5MB metadata image, a 10MB rich-text image, a 50MB office file),
 * so one shared default value could not stand in for all of them.
 *
 * The store is module-level rather than a React context because two consumers are not
 * React: the TipTap paste/drop extension and `content-core`'s `validateFile` both run
 * outside render. React consumers subscribe through `useUploadLimits()`.
 */

import { useSyncExternalStore } from 'react';

export type UploadMediaKind = 'image' | 'audio' | 'video' | 'document';

export interface UploadLimits {
    /** The server's ONE cap for `POST /upload` (media + documents), in MB. */
    mediaMaxSizeMb: number | null;
    /** Extensions (no dot, lower case) the media endpoint accepts, per kind. */
    mediaExtensions: Record<UploadMediaKind, string[]> | null;
    documentMaxSizeMb: number | null;
    scormMaxSizeMb: number | null;
    h5pMaxSizeMb: number | null;
    moodleMaxSizeMb: number | null;
    aiDocumentMaxSizeMb: number | null;
    model3dMaxSizeMb: number | null;
    programDocumentMaxSizeMb: number | null;
    importMaxFiles: number | null;
}

export const UNKNOWN_UPLOAD_LIMITS: UploadLimits = Object.freeze({
    mediaMaxSizeMb: null,
    mediaExtensions: null,
    documentMaxSizeMb: null,
    scormMaxSizeMb: null,
    h5pMaxSizeMb: null,
    moodleMaxSizeMb: null,
    aiDocumentMaxSizeMb: null,
    model3dMaxSizeMb: null,
    programDocumentMaxSizeMb: null,
    importMaxFiles: null,
}) as UploadLimits;

const MB = 1024 * 1024;

let current: UploadLimits = UNKNOWN_UPLOAD_LIMITS;
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/** Snapshot for non-React callers (TipTap extensions, plain upload helpers). */
export function getUploadLimits(): UploadLimits {
    return current;
}

/** Replace the published limits. Pass `null` to fall back to every site's constant. */
export function setUploadLimits(next: UploadLimits | null): void {
    const value = next ?? UNKNOWN_UPLOAD_LIMITS;
    if (value === current) return;
    current = value;
    listeners.forEach((listener) => listener());
}

/** React read of the published limits; re-renders when the app publishes new ones. */
export function useUploadLimits(): UploadLimits {
    return useSyncExternalStore(subscribe, getUploadLimits, getUploadLimits);
}

function isPositive(value: number | null | undefined): value is number {
    return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/**
 * The server's value when known, else the site's own constant. Use where the server
 * value IS the limit for that upload (SCORM, 3D, import sources).
 */
export function resolveLimitBytes(fallbackBytes: number, serverMb: number | null | undefined): number {
    return isPositive(serverMb) ? serverMb * MB : fallbackBytes;
}

/**
 * The smaller of the site's own cap and the server's cap. Use where the site has a
 * stricter product cap of its own (a 10MB image, a 5MB thumbnail) on top of a server
 * cap that covers a whole endpoint: the site must never allow MORE than the server
 * will accept, and a raised server cap must not loosen a deliberate product cap.
 */
export function capLimitBytes(siteBytes: number, serverMb: number | null | undefined): number {
    return isPositive(serverMb) ? Math.min(siteBytes, serverMb * MB) : siteBytes;
}

/** Whole megabytes for a hint/error message; never shows "0MB" for a sub-MB cap. */
export function bytesToMb(bytes: number): number {
    return Math.max(1, Math.round(bytes / MB));
}

/**
 * `<input accept>` for a media kind: the server's extension list when known, else the
 * caller's fallback (usually a MIME wildcard such as `image/*`).
 */
export function mediaAcceptAttr(
    limits: UploadLimits,
    kind: UploadMediaKind,
    fallback: string
): string {
    const list = limits.mediaExtensions?.[kind];
    if (!list || list.length === 0) return fallback;
    return list.map((ext) => `.${ext.replace(/^\./, '').toLowerCase()}`).join(',');
}
